import {
  User,
  Zap,
  Wrench,
  Server,
  BookOpen,
  GitBranch,
  Box,
  Eye,
  Plus,
  X,
  Pencil,
  Tag,
  ShieldAlert,
  Globe2,
} from 'lucide-react';
import type { AssetType, MarketplaceAsset, SecurityVerdict } from '../../types/marketplace';

const ASSET_ICONS: Record<AssetType, typeof User> = {
  persona: User,
  skill: Zap,
  tool: Wrench,
  mcp: Server,
  knowledge: BookOpen,
  workflow: GitBranch,
};

const ASSET_LABELS: Record<AssetType, string> = {
  persona: '人设',
  skill: '技能',
  tool: '工具',
  mcp: 'MCP 工具',
  knowledge: '知识',
  workflow: '工作流',
};

const SOURCE_LABELS: Record<string, string> = {
  builtin: '内置',
  plugins: '精选插件',
  derived: '衍生',
  custom: '自定义',
  marketplace: '市场',
  manual: '手动',
};

/** 安全扫描风险徽标配置（B11）：SAFE 绿 / CAUTION 黄 / DO_NOT_INSTALL 红 */
const SECURITY_BADGE_STYLES: Record<SecurityVerdict, { label: string; background: string; color: string }> = {
  SAFE: { label: '安全', background: 'var(--color-success)', color: 'var(--text-on-success)' },
  CAUTION: { label: '需注意', background: 'var(--color-warning)', color: '#FFFFFF' },
  DO_NOT_INSTALL: { label: '禁止安装', background: 'var(--color-error)', color: '#FFFFFF' },
};

/** B17 信任档位徽标配置：quarantined 隔离观察期（黄） / active 已激活（中性） / global 全局（绿） */
const TRUST_BADGE_STYLES: Record<string, { label: string; background: string; color: string }> = {
  quarantined: { label: '隔离中', background: 'var(--color-warning)', color: '#FFFFFF' },
  active: { label: '已激活', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' },
  global: { label: '全局', background: 'var(--color-success)', color: 'var(--text-on-success)' },
};

/** 渲染技能信任徽标（B17）；无信任档位（非技能资产）时不渲染 */
function TrustBadge({ trustState }: { trustState?: string }) {
  if (!trustState) return null;
  const style = TRUST_BADGE_STYLES[trustState];
  if (!style) return null;
  return (
    <span
      className="badge"
      title={
        trustState === 'quarantined'
          ? '隔离观察期：自动沉淀的草稿技能，3 次成功使用后晋升为已激活'
          : trustState === 'global'
            ? '高信任：可跨 Agent 推荐复用'
            : '正常可用'
      }
      style={{ background: style.background, color: style.color }}
    >
      {trustState === 'global' && <Globe2 size={10} style={{ marginRight: 3, verticalAlign: -1 }} />}
      {style.label}
    </span>
  );
}

/** 渲染安全扫描风险徽标；无扫描数据时不渲染 */
function SecurityBadge({ scan }: { scan?: MarketplaceAsset['securityScan'] }) {
  if (!scan) return null;
  const style = SECURITY_BADGE_STYLES[scan.verdict];
  if (!style) return null;
  return (
    <span
      className="badge"
      title={`安全扫描：${scan.verdict} ${scan.score}/100（命中 ${scan.findings_total} 项，扫描器 v${scan.scanner_version}）`}
      style={{ background: style.background, color: style.color }}
    >
      <ShieldAlert size={10} style={{ marginRight: 3, verticalAlign: -1 }} />
      {style.label}
    </span>
  );
}

interface AssetCardProps {
  asset: MarketplaceAsset;
  onPreview: (asset: MarketplaceAsset) => void;
  onToggle?: (asset: MarketplaceAsset) => void;
  onApply?: (asset: MarketplaceAsset) => void;
  onEdit?: (asset: MarketplaceAsset) => void;
  /** B17：提供后对非全局技能渲染「设为全局」（权限由调用方控制，后端强制校验） */
  onSetGlobal?: (asset: MarketplaceAsset) => void;
  applyLabel?: string;
  removeLabel?: string;
}

export default function AssetCard({
  asset,
  onPreview,
  onToggle,
  onApply,
  onEdit,
  onSetGlobal,
  applyLabel = '应用',
  removeLabel = '移除',
}: AssetCardProps) {
  const Icon = ASSET_ICONS[asset.type] ?? Box;
  const isApplied = asset.isApplied;
  const isEnabled = asset.isEnabled ?? true;

  return (
    <div
      className="asset-card"
      style={{
        background: 'var(--gradient-card)',
        border: `1px solid ${isApplied ? 'var(--color-success)' : 'var(--glass-border)'}`,
        borderRadius: 'var(--radius-xl)',
        padding: 16,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minHeight: 140,
      }}
    >
      {isApplied && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            right: 0,
            padding: '4px 10px',
            background: 'var(--color-success)',
            color: 'var(--text-on-success)',
            fontSize: 11,
            fontWeight: 600,
            borderTopLeftRadius: 'var(--radius-md)',
            borderBottomLeftRadius: 'var(--radius-md)',
            zIndex: 1,
          }}
        >
          已应用
        </div>
      )}

      {asset.license && (
        <div
          title={`许可证：${asset.license}`}
          style={{
            position: 'absolute',
            top: 12,
            left: 0,
            padding: '4px 10px',
            background: 'var(--bg-tertiary)',
            color: 'var(--text-muted)',
            fontSize: 11,
            fontWeight: 600,
            borderTopRightRadius: 'var(--radius-md)',
            borderBottomRightRadius: 'var(--radius-md)',
            zIndex: 1,
            maxWidth: '50%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {asset.license}
        </div>
      )}

      <div className="flex items-start justify-between" style={{ gap: 12, paddingTop: isApplied || asset.license ? 20 : 0 }}>
        <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 'var(--radius-lg)',
              background: 'rgba(88, 166, 255, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon size={20} color="var(--color-primary)" />
          </div>
          <div style={{ minWidth: 0 }}>
            <h4
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={asset.displayName}
            >
              {asset.displayName}
            </h4>
            <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
              {ASSET_LABELS[asset.type]} · {(asset.source ? SOURCE_LABELS[asset.source] : undefined) || asset.source || '未知来源'}
            </div>
          </div>
        </div>
      </div>

      {asset.description && (
        <p
          className="text-muted"
          style={{
            margin: 0,
            fontSize: 13,
            lineHeight: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {asset.description}
        </p>
      )}

      <div className="flex items-center gap-2" style={{ flexWrap: 'wrap', marginTop: 'auto' }}>
        <SecurityBadge scan={asset.securityScan} />
        <TrustBadge trustState={asset.trustState} />
        {asset.category && <span className="badge info">{asset.category}</span>}
        {asset.presetPack && (
          <span className="badge info" title={`预置资产包：${asset.presetPack}`}>
            预置包 · {asset.presetPack}
          </span>
        )}
        {!isEnabled && <span className="badge secondary">已禁用</span>}
        {asset.tags?.slice(0, 3).map((tag) => (
          <span
            key={tag}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '2px 8px',
              borderRadius: 100,
              background: 'var(--bg-tertiary)',
              color: 'var(--text-secondary)',
              fontSize: 11,
            }}
          >
            <Tag size={10} />
            {tag}
          </span>
        ))}
      </div>

      <div
        className="asset-card-actions"
        style={{
          display: 'flex',
          gap: 8,
          marginTop: 8,
          paddingTop: 12,
          borderTop: '1px solid var(--border-muted)',
        }}
      >
        <button
          className="secondary"
          style={{ padding: '6px 10px', fontSize: 12, flex: 1 }}
          onClick={() => onPreview(asset)}
        >
          <Eye size={13} /> 预览
        </button>
        {onToggle ? (
          <button
            className={isApplied ? 'danger' : 'primary'}
            style={{ padding: '6px 10px', fontSize: 12, flex: 1 }}
            onClick={() => onToggle(asset)}
          >
            {isApplied ? (
              <>
                <X size={13} /> {removeLabel}
              </>
            ) : (
              <>
                <Plus size={13} /> {applyLabel}
              </>
            )}
          </button>
        ) : onApply ? (
          <button
            className="primary"
            style={{ padding: '6px 10px', fontSize: 12, flex: 1 }}
            onClick={() => onApply(asset)}
          >
            <Plus size={13} /> {applyLabel}
          </button>
        ) : null}
        {onEdit && isApplied && (
          <button
            className="secondary"
            style={{ padding: '6px 10px', fontSize: 12 }}
            onClick={() => onEdit(asset)}
          >
            <Pencil size={13} />
          </button>
        )}
        {onSetGlobal && asset.trustState && asset.trustState !== 'global' && (
          <button
            className="secondary"
            style={{ padding: '6px 10px', fontSize: 12, color: 'var(--color-success)', borderColor: 'var(--color-success)' }}
            title="提升为高信任档位，可跨 Agent 推荐复用（OWNER/ADMIN）"
            onClick={() => onSetGlobal(asset)}
          >
            <Globe2 size={13} /> 设为全局
          </button>
        )}
      </div>
    </div>
  );
}
